<template>
  <div>
    <!-- Reset Button -->
    <div v-if="visibleGroups.length > 0" class="mb-2">
      <button @click="resetConfigurator" type="button" class="btn product-detail-configurator-reset">
        <span class="product-detail-configurator-reset-x">✖</span> Konfigurator neu starten
      </button>
    </div>

    <!-- Display Groups -->
    <div v-for="(group, index) in visibleGroups" :key="group.id" class="product-detail-configurator-group">
      <div>
        <label class="product-detail-configurator-group-title">
          {{ group.name }}
          <a v-if="group.modal" type="button" class="additional-article-btn" data-bs-toggle="modal"
             :data-bs-target="'#groupinfo-variant-' + group.id">
            <span class="icon icon-info" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="24" height="24" viewBox="0 0 24 24"><defs><path d="M12 7c.5523 0 1 .4477 1 1s-.4477 1-1 1-1-.4477-1-1 .4477-1 1-1zm1 9c0 .5523-.4477 1-1 1s-1-.4477-1-1v-5c0-.5523.4477-1 1-1s1 .4477 1 1v5zm11-4c0 6.6274-5.3726 12-12 12S0 18.6274 0 12 5.3726 0 12 0s12 5.3726 12 12zM12 2C6.4772 2 2 6.4772 2 12s4.4772 10 10 10 10-4.4772 10-10S17.5228 2 12 2z" id="icons-default-info"></path></defs><use xlink:href="#icons-default-info" fill="#758CA3" fill-rule="evenodd"></use></svg>
            </span>
          </a>
          <div v-if="group.modal" class="modal fade modal-additional-article" :id="'groupinfo-variant-' + group.id"
               tabindex="-1" role="dialog" :aria-labelledby="'groupinfo-variant-' + group.id" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title" :id="'groupinfo-variant-' + group.id">
                    {{ group.modal.title }}
                  </h5>
                  <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div class="modal-body" v-html="group.modal.content"></div>
              </div>
            </div>
          </div>
        </label>
        <div v-if="group.description" class="product-detail-configurator-group-description"><span class="small">{{ group.description }}</span></div>
      </div>

      <div class="configurator-group-wrapper">
        <!-- Dropdown Element -->
        <select
            v-model="selections[group.id]"
            @change="onGroupChange(group.id)"
            class="form-select product-detail-configurator-select-input"
        >
          <option value="">Bitte auswählen</option>
          <option
              v-for="(option, optionId) in sortedOptions(group.id)"
              :key="optionId"
              :value="optionId"
              :disabled="!isOptionEnabled(group.id, optionId)"
          >
            {{ option.name }}{{ !isOptionEnabled(group.id, optionId) ? ' [Kombination nicht möglich, bitte Konfigurator neu starten]' : '' }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>


<script>
import axios from "axios";

export default {
  props: {
    productId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      groups: [],
      products: [],
      resetUrl: '',
      selections: {},
      enabledOptions: {},
      visibleGroups: []
    };
  },
  mounted() {
    this.fetchConfigurations();
  },
  methods: {
    fetchConfigurations() {
      axios
          .get("/rotech-configurator/options", {
            params: {
              productId: this.productId
            }
          })
          .then((response) => {
            const data = response.data;
            this.groups = data.groups.map((group) => ({
              ...group,
              isDisabled: false // New property: Initially not disabled
            }));
            this.products = data.products || [];
            this.resetUrl = data.resetUrl || window.location.pathname;
            this.initializeSelections();

            const productIdFromUrl = this.getProductIdFromUrl();
            if (productIdFromUrl) {
              this.prefillConfiguration(productIdFromUrl);
            } else {
              this.resetConfiguratorState();
              // Make all groups visible, not just the first one
              if (this.groups.length > 0) {
                this.visibleGroups = [...this.groups];
              }
            }
          })
          .catch((error) => {
            console.error("Fehler beim Abrufen:", error);
          });
    },
    initializeSelections() {
      this.groups.forEach((group) => {
        this.selections[group.id] = "";
        this.enabledOptions[group.id] = Object.keys(group.options);
      });
    },
    isOptionEnabled(groupId, optionId) {
      return this.enabledOptions[groupId]?.includes(optionId);
    },
    onGroupChange(changedGroupId) {
      // Don't lock any groups anymore, instead revalidate all options

      // Check if all groups are configured
      const allGroupsConfigured = this.groups.every((group) => this.selections[group.id]);
      if (allGroupsConfigured) {
        this.finalizeConfiguration();
        return;
      }

      // Update the valid options for all groups
      this.updateValidOptionsForAllGroups();
    },

    updateValidOptionsForAllGroups() {
      // Calculate valid options for each group
      this.groups.forEach((group) => {
        // Find all products that are compatible with the current selections
        // (except for the current group we are validating)
        const validProducts = this.products.filter((product) =>
            Object.keys(this.selections).every((groupId) => {
              // If it's the current group we're validating, ignore this selection
              if (groupId === group.id) return true;

              const optionId = this.selections[groupId];
              return optionId ? product.optionIds.includes(optionId) : true;
            })
        );

        // Collect all valid options for this group
        const allowedOptions = new Set();
        validProducts.forEach((product) => {
          product.optionIds.forEach((optionId) => {
            if (group.options[optionId]) {
              allowedOptions.add(optionId);
            }
          });
        });

        this.enabledOptions[group.id] = Array.from(allowedOptions);
      });
    },
    validateOptions() {
      // Use the new method to validate all options
      this.updateValidOptionsForAllGroups();
    },
    resetConfiguratorState() {
      // 1. Initialize the configuration and show all groups
      this.initializeSelections();
      this.visibleGroups = [...this.groups];
      this.groups.forEach((group) => {
        group.isDisabled = false;
      });

      // Update the valid options for all groups
      this.updateValidOptionsForAllGroups();

      // 2. Hide the additional articles (if displayed)
      const additionalArticleDiv = document.querySelector('.additional-article');
      if (additionalArticleDiv) {
        additionalArticleDiv.classList.add('d-none');
      }

      // 3. Disable buttons with classes 'btn-buy', 'btn-plus' and 'btn-minus'
      const buttons = document.querySelectorAll('.btn-buy, .btn-plus, .btn-minus');
      buttons.forEach((button) => {
        button.disabled = true;
      });

      // 4. Disable quantity input field with class 'quantity-selector-group-input'
      const quantityInput = document.querySelector('.quantity-selector-group-input');
      if (quantityInput) {
        quantityInput.disabled = true;
      }
    },
    async resetConfigurator(event) {
      if (event) event.preventDefault();

      const resetUrl = new URL(this.resetUrl || window.location.pathname, window.location.origin);
      resetUrl.searchParams.delete('productId');
      resetUrl.searchParams.set('options', '');
      window.location.href = resetUrl.toString();
    },
    getProductIdFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get("productId");
    },
    prefillConfiguration(productId) {
      const matchingProduct = this.products.find((product) => product.id === productId);
      if (!matchingProduct) {
        console.error("No product found with the specified product ID.");
        return;
      }

      this.groups.forEach((group) => {
        const optionId = matchingProduct.optionIds.find((optionId) => group.options[optionId]);
        if (optionId) {
          this.selections[group.id] = optionId;
          // Don't disable any groups anymore
          group.isDisabled = false;
        }
      });

      this.visibleGroups = [...this.groups];

      const additionalArticleDiv = document.querySelector('.additional-article');
      if (additionalArticleDiv) {
        additionalArticleDiv.classList.remove('d-none'); // Display, as the configuration was filled via productId
      }

      // Update the valid options for all groups
      this.updateValidOptionsForAllGroups();
    },
    finalizeConfiguration() {
      const selectedOptions = Object.values(this.selections);
      const matchingProduct = this.products.find((product) =>
          selectedOptions.every((optionId) => product.optionIds.includes(optionId))
      );

      if (matchingProduct) {
        const targetUrl = `${matchingProduct.link}?productId=${matchingProduct.id}`;
        window.location.href = targetUrl;
      } else {
        console.error("No matching product found.");
      }
    },
    sortedOptions(groupId) {
      const group = this.groups.find(g => g.id === groupId);
      let options = Object.entries(group.options);
      options.sort(([aId, a], [bId, b]) => {
        if (this.isOptionEnabled(groupId, aId) && !this.isOptionEnabled(groupId, bId))
          return -1;
        if (!this.isOptionEnabled(groupId, aId) && this.isOptionEnabled(groupId, bId))
          return 1;
        return 0;
      });

      // The returned value is an array of arrays, each with format [optionId, option]
      return Object.fromEntries(options);
    }
  }
};
</script>

<style scoped>
/* Custom CSS */
</style>
